import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import Spin from 'antd/es/spin';
import Tag from 'antd/es/tag';
import theme from 'antd/es/theme';
import Typography from 'antd/es/typography';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import styled, { keyframes, useTheme } from 'styled-components';
import tinycolor from 'tinycolor2';

const pulse = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.3;
  }
`;

const StreamingDot = styled.span`
  ${({ theme }) => `
  display: inline-block;
  width: ${theme.sizeXS - 2}px;
  height: ${theme.sizeXS - 2}px;
  border-radius: 50%;
  background-color: ${theme.colorPrimary};
  margin-left: ${theme.marginXXS}px;
  vertical-align: middle;
  animation: ${pulse} 1.5s ease-in-out infinite;
`}
`;

const StyledStreamingTag = styled(Tag)`
  ${({ theme }) => `
  display: inline-flex;
  align-items: center;
  gap: ${theme.marginXXS}px;
  flex-wrap: nowrap;
  width: fit-content;
  max-width: 100%;
`}
`;

const StreamingText = styled.span`
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const MarkdownContainer = styled.div`
  ${({ theme }) => `
  .markdown p {
    margin: ${theme.marginXXS}px 0;
  }
`}
`;

// Add styled components for tables
const StyledTable = styled.table`
  ${({ theme }) => `
  width: 100%;
  margin: ${theme.marginXXS}px 0;
  border-collapse: collapse;
  font-size: ${theme.fontSizeSM}px;
  border: ${theme.lineWidth}px solid ${theme.colorBorder};
  border-radius: ${theme.borderRadius}px;
  overflow: hidden;
  line-height: ${theme.lineHeight};
`}
`;

const StyledTableHead = styled.thead`
  ${({ theme }) => `
  background-color: ${theme.colorFillAlter};
`}
`;

const StyledTableHeaderCell = styled.th`
  ${({ theme }) => `
  padding: ${theme.paddingXS}px ${theme.paddingSM}px;
  text-align: left;
  font-weight: ${theme.fontWeightStrong};
  color: ${theme.colorTextDescription};
  border-bottom: ${theme.lineWidth}px solid ${theme.colorBorder};
`}
`;

const StyledTableBody = styled.tbody`
  ${({ theme }) => `
  background-color: ${theme.colorBgContainer};

  tr:hover {
    background-color: ${theme.colorFillAlter};
  }
`}
`;

const StyledTableRow = styled.tr`
  ${({ theme }) => `
  &:not(:last-child) {
    border-bottom: ${theme.lineWidth}px solid ${theme.colorBorder};
  }
`}
`;

const StyledTableCell = styled.td`
  ${({ theme }) => `
  padding: ${theme.paddingXS}px ${theme.paddingSM}px;
  border-bottom: ${theme.lineWidth}px solid ${theme.colorBorder};
`}
`;

const StyledCodeBlock = styled.div`
  ${({ theme }) => `
  margin: ${theme.marginSM}px 0;
  border-radius: ${theme.borderRadius}px;
  overflow: hidden;
  font-size: ${theme.fontSizeSM}px;
  line-height: ${theme.lineHeightLG};
  
  pre {
    margin: 0 !important;
    padding: ${theme.paddingSM}px !important;
  }
`}
`;

const InlineCode = styled.code`
  ${({ theme }) => `
  padding: ${theme.paddingXXS / 2}px ${theme.paddingXXS}px;
  font-size: ${theme.fontSizeSM}px;
  background-color: ${theme.colorFillSecondary};
  border-radius: ${theme.borderRadiusXS}px;
  font-family: ${theme.fontFamilyCode};
`}
`;

const MarkdownContent = React.memo(
  ({
    content,
    isStreaming,
    streamingStatus,
  }: {
    content: string;
    isStreaming?: boolean;
    streamingStatus?: string | null;
  }) => {
    const { token } = theme.useToken();
    const styledTheme = useTheme();

    // Detect if we're in dark mode
    const isDarkMode = React.useMemo(() => {
      const bgColor = tinycolor(styledTheme.colorBgBase || '#ffffff');
      return bgColor.getBrightness() < 128;
    }, [styledTheme]);

    // Simple content processing - just add streaming marker if needed
    const processedContent = isStreaming
      ? `${content} [](streaming.example.com)`
      : content;

    return (
      <MarkdownContainer>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ node: _, ...props }) => (
              <p
                {...props}
                style={{
                  marginTop: `${styledTheme.marginXXS}px`,
                  marginBottom: `${styledTheme.marginXXS}px`,
                }}
              />
            ),
            a: ({ node: _, ...props }) => {
              // Handle streaming indicator
              if (props.href === 'streaming.example.com') {
                return streamingStatus ? (
                  <StyledStreamingTag
                    color={token.colorPrimary}
                    bordered={false}
                  >
                    <Spin indicator={<LoadingOutlined spin />} size="small" />
                    <StreamingText>{streamingStatus}</StreamingText>
                  </StyledStreamingTag>
                ) : (
                  <StreamingDot />
                );
              }
              return <a {...props} />;
            },
            h1: ({ node: _, ...props }) => (
              <Typography.Title
                level={2}
                style={{
                  marginTop: `${styledTheme.marginXXS}px`,
                  fontSize: `${styledTheme.fontSize}px`,
                }}
                {...props}
              />
            ),
            h2: ({ node: _, ...props }) => (
              <Typography.Title
                level={3}
                style={{
                  marginTop: `${styledTheme.margin}px`,
                  fontSize: `${styledTheme.fontSize}px`,
                }}
                {...props}
              />
            ),
            h3: ({ node: _, ...props }) => (
              <Typography.Title
                level={4}
                style={{
                  marginTop: `${styledTheme.margin}px`,
                  fontSize: `${styledTheme.fontSizeSM}px`,
                }}
                {...props}
              />
            ),
            h4: ({ node: _, ...props }) => (
              <Typography.Title
                level={5}
                style={{
                  marginTop: `${styledTheme.margin}px`,
                  fontSize: `${styledTheme.fontSizeSM}px`,
                }}
                {...props}
              />
            ),
            // Table components
            table: ({ node: _, ...props }) => <StyledTable {...props} />,
            thead: ({ node: _, ...props }) => <StyledTableHead {...props} />,
            tbody: ({ node: _, ...props }) => <StyledTableBody {...props} />,
            tr: ({ node: _, ...props }) => <StyledTableRow {...props} />,
            th: ({ node: _, ...props }) => <StyledTableHeaderCell {...props} />,
            td: ({ node: _, ...props }) => <StyledTableCell {...props} />,
            // Code components
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            code: ({ inline, className, children, ...props }: any) => {
              const match = /language-(\w+)/.exec(className || '');
              const language = match ? match[1] : 'python'; // Default to Python

              return !inline && match ? (
                <StyledCodeBlock>
                  <SyntaxHighlighter
                    style={isDarkMode ? oneDark : oneLight}
                    language={language}
                    PreTag="pre"
                    showLineNumbers={false}
                    customStyle={{
                      fontSize: `${styledTheme.fontSizeSM}px`,
                      lineHeight: styledTheme.lineHeight,
                      margin: 0,
                    }}
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                </StyledCodeBlock>
              ) : (
                <InlineCode className={className} {...props}>
                  {children}
                </InlineCode>
              );
            },
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </MarkdownContainer>
    );
  },
);

export default MarkdownContent;
